import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  mdiAlert,
  mdiArrowLeft,
  mdiArrowRight,
  mdiCheckDecagram,
} from "@mdi/js";
import Icon from "@mdi/react";
import { Alert, Button, Flex, Input, notification } from "antd";
import { observer } from "mobx-react-lite";

import { api } from "../../api";
import AuthCard from "../../components/auth/AuthCard";
import { loginStore } from "../../stores/loginStore";
import { userStore } from "../../stores/userStore";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";

import "./PasswordPage.css";

export const PasswordPage = observer(function PasswordPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  usePageTitle(t("password.title"));
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [wrongCredentials, setWrongCredentials] = useState(false);
  const { pending: loading, run: runSignIn } = usePendingAction();

  useEffect(() => {
    if (!loginStore.email) {
      navigate("/login/1", { replace: true });
    }
  }, [navigate]);

  async function handleSignIn() {
    await runSignIn(async () => {
      setWrongCredentials(false);
      try {
        const result = await api.auth.login(loginStore.email, password);
        if (result.ok) {
          userStore.clear();
          if (loginStore.next) {
            const next = loginStore.next;
            if (next.startsWith("contact")) {
              navigate(`/profile?next=${next}`);
            } else if (next.startsWith("/") && !next.startsWith("/oauth2")) {
              navigate(next, { state: { loggedIn: true }, replace: true });
            } else {
              window.location.href = next;
            }
          } else {
            navigate("/profile");
          }
        } else if (result.code === "wrong_credentials") {
          setWrongCredentials(true);
        }
      } catch {
        notification.error({
          message: t("errors.shared.common_error"),
          placement: "topRight",
        });
      }
    });
  }

  return (
    <AuthCard>
      <Flex justify="space-between" align="start">
        <h1>{t("password.title")}</h1>
        <Link
          to="/login/1"
          onClick={() => loginStore.clear()}
          className="password-goback--desktop"
        >
          {t("password.goBack")}
        </Link>
      </Flex>

      <span className="email-chip">
        <Icon color="#4caf50" path={mdiCheckDecagram} size={0.7} />
        {loginStore.email}
      </span>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSignIn();
        }}
      >
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={loginStore.email}
          readOnly
          style={{ display: "none" }}
        />
        <Input.Password
          name="password"
          placeholder={t("password.yourPassword")}
          size="large"
          autoFocus
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (wrongCredentials) setWrongCredentials(false);
          }}
          onPressEnter={handleSignIn}
        />

        {wrongCredentials && (
          <Alert
            type="error"
            showIcon
            icon={<Icon path={mdiAlert} size={0.75} color="#ff4d4f" />}
            style={{ marginTop: 16 }}
            className="password-alert"
            classNames={{ description: "password-alert__description" }}
            description={t("password.wrongCredentials")}
          />
        )}

        <Flex gap={12} align="center" wrap="wrap" style={{ marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleSignIn}
            className="password-actions__signin"
          >
            {t("signin.buttonText")}
          </Button>

          <span className="password-actions__spacer" />
          <Link to="/password/reset" className="password-actions__forgot">
            {t("signin.forgotPassword")}
          </Link>
        </Flex>
      </form>

      <div className="password-mobile-links">
        <Link
          to="/signup/0"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          {t("signup.title")}
        </Link>
        <br />
        <Link to="/password/reset">{t("signin.forgotPassword")}</Link>
        <br />
        <Link
          to="/login/1"
          onClick={() => loginStore.clear()}
          className="password-goback--mobile"
        >
          <Icon path={isRtl ? mdiArrowRight : mdiArrowLeft} size={0.6} />
          {t("password.goBack")}
        </Link>
      </div>
    </AuthCard>
  );
});
